/**
 * UR Brand System — Phase 4 reviewer workspace.
 *
 * One admin screen. Attention-first. Minimal clicks:
 *   - compact project header
 *   - "[N] items need your attention" (click to jump)
 *   - six section cards (status, statements, APPROVE / EDIT / FLAG,
 *     REGENERATE DRAFT, specificity check inline for load-bearing)
 *   - UR Quality Gate panel (5x PASS / NEEDS WORK)
 *   - Clarifications + Escalation
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = [
  ["business_snapshot", "01 Business Snapshot"],
  ["audience_interpretation", "02 Audience"],
  ["competitive_context", "03 Competitive Context"],
  ["positioning_value_proposition", "04 Positioning & Value"],
  ["brand_character_messaging", "05 Character & Messaging"],
  ["creative_direction", "06 Creative Direction"],
];
const LOAD_BEARING = new Set(["positioning_value_proposition", "creative_direction"]);
const QG_CRITERIA = [
  ["grounded", "Grounded", "Claims traceable to evidence."],
  ["specific", "Specific", "Meaningfully specific to this business."],
  ["useful", "Useful", "Helps a designer make better decisions."],
  ["coherent", "Coherent", "Audience → positioning → character → messaging → direction align."],
  ["ur_standard", "UR Standard", "Comfortable presenting as professional Ur Designs strategy."],
];

const statusBadge = (status) => {
  const map = {
    approved: "bg-green-500/10 text-green-300 border-green-500/30",
    needs_review: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    stale: "bg-red-500/10 text-red-300 border-red-500/30",
    draft: "bg-neutral-500/10 text-neutral-400 border-neutral-700",
  };
  return map[status] || map.draft;
};

export default function BrandStrategyReview() {
  const { projectId } = useParams();
  const sessionToken = typeof document !== "undefined"
    ? (document.cookie.match(/admin_session=([^;]+)/) || [])[1]
    : null;
  const api = useMemo(() => axios.create({
    baseURL: `${BASE}/api/admin/brand-projects`,
    withCredentials: true,
    headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
  }), [sessionToken]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const sectionRefs = useRef({});

  const refresh = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/${projectId}/strategy/review`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load review.");
    } finally { setLoading(false); }
  }, [api, projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  const snapshot = data?.snapshot;
  const sid = snapshot?.snapshot_id;

  const call = async (label, fn) => {
    setBusy(label); setErr("");
    try { await fn(); await refresh(); }
    catch (e) { setErr(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(""); }
  };

  const generate = () => call("Generating draft…",
    () => api.post(`/${projectId}/strategy/generate`, { snapshot_id: sid }));
  const regenerate = (section) => call(`Regenerating ${section}…`,
    () => api.post(`/${projectId}/strategy/snapshots/${sid}/regenerate`, { section }));
  const approveSection = (section, specificity) => call(`Approving ${section}…`,
    () => api.post(`/${projectId}/strategy/snapshots/${sid}/sections/${section}/approve`,
      LOAD_BEARING.has(section) ? { specificity_check_passed: !!specificity } : {}));
  const flagSection = (section) => call(`Flagging ${section}…`,
    () => api.post(`/${projectId}/strategy/snapshots/${sid}/sections/${section}/flag`));
  const classifyRi = (statementId) => call("Marking reasonable interpretation…",
    () => api.post(
      `/${projectId}/strategy/snapshots/${sid}/statements/${statementId}/classify`,
      { classification: "REASONABLE_INTERPRETATION" }));
  const editStatement = (statementId, content) => call("Saving edit…",
    () => api.post(
      `/${projectId}/strategy/snapshots/${sid}/statements/${statementId}/reviewer-edit`,
      { content }));
  const saveQualityGate = (gate) => call("Saving quality gate…",
    () => api.post(`/${projectId}/strategy/snapshots/${sid}/quality-gate`, gate));
  const setEscalation = (payload) => call("Updating escalation…",
    () => api.post(`/${projectId}/escalate`, payload));
  const createClarification = (payload) => call("Creating clarification…",
    () => api.post(`/${projectId}/clarifications`, payload));
  const resolveClarification = (cid) => call("Resolving…",
    () => api.post(`/${projectId}/clarifications/${cid}/resolve`, { answer: "" }));

  const jumpTo = (section) => {
    const el = sectionRefs.current[section];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) return <Shell><p className="text-neutral-500">Loading…</p></Shell>;
  if (err && !data) return <Shell><p className="text-red-400">{err}</p></Shell>;
  if (!data) return <Shell><p className="text-neutral-500">No data.</p></Shell>;

  const { project, attention, readiness, statements_by_section, clarifications } = data;
  const sections = snapshot?.sections || {};

  return (
    <Shell>
      {busy && <div className="fixed top-0 inset-x-0 bg-orange-500 text-black text-xs px-4 py-1 z-50" data-testid="strategy-busy">{busy}</div>}
      {err && <div className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm px-4 py-2 mb-4 rounded" data-testid="strategy-error">{err}</div>}

      {/* Header */}
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-white" data-testid="strategy-business-name">
          {project.business_name || project.project_id}
        </h1>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-400 mt-2">
          <span>Stage: <b className="text-neutral-200">{project.business_stage || "—"}</b></span>
          <span>Existing identity: <b className="text-neutral-200">{project.existing_identity ? "yes" : "no"}</b></span>
          <span>Language: <b className="text-neutral-200">{project.primary_language || "—"}</b></span>
          <span>Reviewer: <b className="text-neutral-200">{project.reviewer_assigned_to || "—"}</b> ({project.reviewer_role || "—"})</span>
          <span>Status: <b className="text-orange-400" data-testid="strategy-project-status">{project.project_status}</b></span>
          <span>Intake rev: <b className="text-neutral-200">{project.intake_revision}</b></span>
        </div>
      </header>

      {/* Attention */}
      <div className="mb-6 rounded border border-neutral-800 bg-neutral-950">
        <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
          <div className="text-sm uppercase tracking-wider text-orange-400" data-testid="strategy-attention-count">
            {attention.count} {attention.count === 1 ? "item needs" : "items need"} your attention
          </div>
          {!snapshot?.quality_gate?.reviewer && (
            <button type="button" onClick={generate} className="text-xs px-3 py-1 bg-orange-500 text-black rounded hover:bg-orange-600" data-testid="strategy-generate">
              Generate draft
            </button>
          )}
        </div>
        <ul className="divide-y divide-neutral-900 max-h-64 overflow-auto">
          {attention.items.length === 0 && (
            <li className="px-4 py-3 text-xs text-neutral-500">Nothing outstanding.</li>
          )}
          {attention.items.map((it, i) => (
            <li key={i} className="px-4 py-2 text-xs flex justify-between items-center hover:bg-neutral-900/40">
              <span className="text-neutral-200">
                <span className="text-neutral-500 mr-2">[{it.kind}]</span>
                {it.label || it.section}
              </span>
              {it.section && (
                <button type="button" onClick={() => jumpTo(it.section)} className="text-orange-400 hover:text-orange-300" data-testid={`strategy-attention-jump-${i}`}>Go</button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Sections */}
      {SECTION_META.map(([key, label]) => (
        <SectionCard
          key={key} id={key} label={label}
          innerRef={(el) => { sectionRefs.current[key] = el; }}
          section={sections[key]}
          statements={statements_by_section?.[key] || []}
          onApprove={(spec) => approveSection(key, spec)}
          onFlag={() => flagSection(key)}
          onRegenerate={() => regenerate(key)}
          onEdit={editStatement}
          onClassifyRi={classifyRi}
          onClarify={(q, blocking) => createClarification({
            section: key, kind: "customer_clarification", question: q, blocking,
          })}
        />
      ))}

      {/* Quality Gate */}
      <QualityGate current={snapshot?.quality_gate || {}} readiness={readiness} onSave={saveQualityGate} />

      {/* Clarifications */}
      <Clarifications items={clarifications} onResolve={resolveClarification} />

      {/* Escalation */}
      <Escalation project={project} onChange={setEscalation} />
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const Shell = ({ children }) => (
  <div className="min-h-screen bg-black text-neutral-100">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <div className="text-[11px] uppercase tracking-widest text-orange-500 mb-4">
        UR Designs · Brand Strategy Review
      </div>
      {children}
    </div>
  </div>
);

const SectionCard = ({ id, label, innerRef, section, statements, onApprove, onFlag, onRegenerate, onEdit, onClassifyRi, onClarify }) => {
  const status = section?.status || "draft";
  const [specificity, setSpecificity] = useState(section?.specificity_check_passed || false);
  const [clarifyQ, setClarifyQ] = useState("");
  return (
    <section ref={innerRef} className="mb-5 rounded border border-neutral-800 bg-neutral-950" data-testid={`strategy-section-${id}`}>
      <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
        <div className="text-sm font-medium text-white">{label}</div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded ${statusBadge(status)}`} data-testid={`strategy-section-status-${id}`}>{status}</span>
          <button type="button" onClick={onRegenerate} className="text-xs px-2 py-1 border border-neutral-700 rounded hover:bg-neutral-900" data-testid={`strategy-regenerate-${id}`}>Regenerate draft</button>
        </div>
      </div>
      <ul className="divide-y divide-neutral-900">
        {statements.length === 0 && <li className="px-4 py-3 text-xs text-neutral-500">No statements yet.</li>}
        {statements.map((s) => (
          <StatementRow key={s.statement_id} s={s} onEdit={onEdit} onClassifyRi={onClassifyRi} />
        ))}
      </ul>
      <div className="px-4 py-3 border-t border-neutral-800 flex flex-wrap items-center gap-3">
        {id in {positioning_value_proposition: 1, creative_direction: 1} && (
          <label className="text-xs text-neutral-300 flex items-center gap-2">
            <input type="checkbox" checked={specificity} onChange={(e) => setSpecificity(e.target.checked)} data-testid={`strategy-specificity-${id}`} />
            Specific enough to this business? Yes — supported by meaningful differences/evidence.
          </label>
        )}
        <button type="button" onClick={() => onApprove(specificity)} className="text-xs px-3 py-1 bg-green-600 text-black rounded hover:bg-green-500 ml-auto" data-testid={`strategy-approve-${id}`}>Approve</button>
        <button type="button" onClick={onFlag} className="text-xs px-3 py-1 border border-amber-500/50 text-amber-200 rounded hover:bg-amber-500/10" data-testid={`strategy-flag-${id}`}>Flag</button>
      </div>
      <div className="px-4 pb-3 flex gap-2">
        <input value={clarifyQ} onChange={(e) => setClarifyQ(e.target.value)} placeholder="Ask the customer a clarification…"
          className="flex-1 bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-xs text-white"
          data-testid={`strategy-clarify-input-${id}`} />
        <button type="button" disabled={!clarifyQ.trim()}
          onClick={() => { onClarify(clarifyQ.trim(), true); setClarifyQ(""); }}
          className="text-xs px-3 py-1 border border-neutral-700 rounded hover:bg-neutral-900 disabled:opacity-40"
          data-testid={`strategy-clarify-send-${id}`}>Request clarification</button>
      </div>
    </section>
  );
};

const StatementRow = ({ s, onEdit, onClassifyRi }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s.content);
  const [showSources, setShowSources] = useState(false);
  return (
    <li className="px-4 py-3" data-testid={`strategy-statement-${s.statement_id}`}>
      {editing ? (
        <>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
            className="w-full bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-sm text-white" />
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => { onEdit(s.statement_id, draft); setEditing(false); }} className="text-xs px-3 py-1 bg-orange-500 text-black rounded" data-testid={`strategy-edit-save-${s.statement_id}`}>Save</button>
            <button type="button" onClick={() => { setDraft(s.content); setEditing(false); }} className="text-xs px-3 py-1 border border-neutral-700 rounded">Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div className="text-sm text-neutral-100 leading-relaxed">{s.content || <span className="text-neutral-500">(empty)</span>}</div>
          <div className="mt-1 text-[11px] text-neutral-500 flex flex-wrap gap-x-3 gap-y-1 items-center">
            <span>{s.statement_type}</span>
            <span>·</span>
            <span className={s.status === "SUPPORTED" ? "text-green-400" : s.status === "REASONABLE_INTERPRETATION" ? "text-sky-300" : "text-amber-300"}>{s.status}</span>
            {s.stale && <span className="text-red-300">· stale</span>}
            {s.claim_flags?.length > 0 && <span className="text-amber-300">· {s.claim_flags.join(", ")}</span>}
            {s.edited_by_reviewer && <span>· edited by reviewer</span>}
            <button type="button" onClick={() => setShowSources(!showSources)} className="underline hover:text-neutral-200" data-testid={`strategy-sources-toggle-${s.statement_id}`}>
              {showSources ? "Hide sources" : `View sources (${(s.references || []).length})`}
            </button>
            <button type="button" onClick={() => setEditing(true)} className="underline hover:text-neutral-200" data-testid={`strategy-edit-${s.statement_id}`}>Edit</button>
            {s.status === "NEEDS_REVIEW" && (
              <button type="button" onClick={() => onClassifyRi(s.statement_id)} className="underline hover:text-neutral-200" data-testid={`strategy-ri-${s.statement_id}`}>Reasonable interpretation</button>
            )}
          </div>
          {showSources && (
            <ul className="mt-2 text-[11px] text-neutral-400 space-y-1" data-testid={`strategy-sources-${s.statement_id}`}>
              {(s.references || []).map((r, i) => (
                <li key={i}>• {r.ref_type}: {r.ref_path || r.ref_id}</li>
              ))}
              {(s.references || []).length === 0 && <li className="text-neutral-600">No references.</li>}
            </ul>
          )}
        </>
      )}
    </li>
  );
};

const QualityGate = ({ current, readiness, onSave }) => {
  const [state, setState] = useState({
    grounded: current.grounded || "",
    specific: current.specific || "",
    useful: current.useful || "",
    coherent: current.coherent || "",
    ur_standard: current.ur_standard || "",
  });
  useEffect(() => {
    setState({
      grounded: current.grounded || "", specific: current.specific || "",
      useful: current.useful || "", coherent: current.coherent || "",
      ur_standard: current.ur_standard || "",
    });
  }, [current.grounded, current.specific, current.useful, current.coherent, current.ur_standard]);
  const canSave = Object.values(state).every((v) => v === "PASS" || v === "NEEDS_WORK");
  return (
    <section className="mt-8 rounded border border-neutral-800 bg-neutral-950" data-testid="strategy-quality-gate">
      <div className="px-4 py-3 border-b border-neutral-800 flex justify-between items-center">
        <div className="text-sm font-medium text-white">UR Quality Gate</div>
        {readiness && (
          <span className={`text-[11px] px-2 py-0.5 border rounded ${readiness.ready ? "border-green-500/40 text-green-300" : "border-amber-500/40 text-amber-200"}`} data-testid="strategy-ready-badge">
            {readiness.ready ? "Strategy ready" : "Not ready"}
          </span>
        )}
      </div>
      <ul className="divide-y divide-neutral-900">
        {QG_CRITERIA.map(([key, label, help]) => (
          <li key={key} className="px-4 py-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm text-neutral-100">{label}</div>
              <div className="text-xs text-neutral-500">{help}</div>
            </div>
            <div className="flex gap-2">
              {["PASS", "NEEDS_WORK"].map((v) => (
                <button key={v} type="button" onClick={() => setState({ ...state, [key]: v })}
                  className={`text-xs px-2 py-1 border rounded ${state[key] === v ? (v === "PASS" ? "bg-green-500/15 border-green-500/40 text-green-200" : "bg-amber-500/15 border-amber-500/40 text-amber-200") : "border-neutral-700 text-neutral-400 hover:bg-neutral-900"}`}
                  data-testid={`strategy-qg-${key}-${v.toLowerCase()}`}>
                  {v === "PASS" ? "Pass" : "Needs work"}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
      <div className="px-4 py-3 border-t border-neutral-800 flex justify-end gap-3">
        <button type="button" disabled={!canSave} onClick={() => onSave(state)}
          className="text-xs px-3 py-1 bg-orange-500 text-black rounded hover:bg-orange-600 disabled:opacity-40"
          data-testid="strategy-qg-save">Save</button>
      </div>
      {readiness && !readiness.ready && (
        <ul className="px-4 pb-3 text-[11px] text-neutral-500 space-y-1" data-testid="strategy-ready-reasons">
          {readiness.reasons.map((r, i) => (<li key={i}>• {r}</li>))}
        </ul>
      )}
    </section>
  );
};

const Clarifications = ({ items, onResolve }) => (
  <section className="mt-6 rounded border border-neutral-800 bg-neutral-950" data-testid="strategy-clarifications">
    <div className="px-4 py-3 border-b border-neutral-800 text-sm font-medium text-white">Open clarifications</div>
    <ul className="divide-y divide-neutral-900">
      {(items || []).length === 0 && <li className="px-4 py-3 text-xs text-neutral-500">No open clarifications.</li>}
      {(items || []).map((c) => (
        <li key={c.clarification_id} className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-neutral-100">{c.question}</div>
            <div className="text-xs text-neutral-500">{c.section} · {c.kind}{c.blocking ? " · blocking" : ""}</div>
          </div>
          <button type="button" onClick={() => onResolve(c.clarification_id)} className="text-xs px-2 py-1 border border-neutral-700 rounded hover:bg-neutral-900">Resolve</button>
        </li>
      ))}
    </ul>
  </section>
);

const Escalation = ({ project, onChange }) => {
  const [reason, setReason] = useState(project.escalation_reason || "unresolved_differentiation");
  const active = project.escalation_required;
  return (
    <section className="mt-6 rounded border border-neutral-800 bg-neutral-950" data-testid="strategy-escalation">
      <div className="px-4 py-3 border-b border-neutral-800 text-sm font-medium text-white">Escalation</div>
      <div className="px-4 py-3 flex flex-wrap items-center gap-3 text-xs">
        <select value={reason} onChange={(e) => setReason(e.target.value)}
          className="bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-neutral-200"
          data-testid="strategy-escalation-reason">
          <option value="unresolved_differentiation">Unresolved differentiation</option>
          <option value="reputational_risk">Reputational risk</option>
          <option value="regulated_or_sensitive_claim">Regulated / sensitive claim</option>
          <option value="cultural_or_language_uncertainty">Cultural / language uncertainty</option>
          <option value="major_strategic_contradiction">Major strategic contradiction</option>
          <option value="reviewer_requests_owner_judgment">Reviewer requests owner judgment</option>
          <option value="other">Other</option>
        </select>
        {active ? (
          <button type="button" onClick={() => onChange({ escalation_required: false })} className="px-3 py-1 border border-neutral-700 rounded hover:bg-neutral-900" data-testid="strategy-escalation-clear">Clear</button>
        ) : (
          <button type="button" onClick={() => onChange({ escalation_required: true, escalation_reason: reason })} className="px-3 py-1 bg-amber-500/15 border border-amber-500/40 text-amber-200 rounded hover:bg-amber-500/25" data-testid="strategy-escalation-raise">Raise escalation</button>
        )}
        {active && <span className="text-amber-300">Current: {project.escalation_reason}</span>}
      </div>
    </section>
  );
};
